/*
Copyright (C) 2024 Laboratorio Visao Robotica e Imagem

Departamento de Informatica - Universidade Federal do Parana - VRI/UFPR

This file is part of CienciaNaEscola. CienciaNaEscola is free software: you can redistribute it and/or modify it under the terms of the GNU
General Public License as published by the Free Software Foundation, either version 3 of the License, or (at your option) any later version.
CienciaNaEscola is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU General Public License for more details. You should have received a copy
of the GNU General Public License along with CienciaNaEscola.  If not, see <https://www.gnu.org/licenses/>
*/

import { useContext, useState } from 'react';
import CheckIcon from '../assets/images/CheckIcon.svg';
import RoundedButton from './RoundedButton';
import { AlertContext } from '../contexts/AlertContext';
import MaterialSymbol from './MaterialSymbol';

const styles = `
    .font-barlow {
        font-family: 'Barlow', sans-serif;
    }    

    .custom-btn {
        background: #f9fbfb;
        cursor: pointer;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
        transition: 0.3s;
    }

    .custom-btn:hover {
        background: #fff;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
        transform: translateY(-2px);
    }

    .custom-action-btn {
        transition: filter 0.2s ease, transform 0.1s ease;
    }

    .custom-action-btn:hover {
        filter: brightness(0.85);
    }

    .custom-action-btn:active {
        transform: scale(0.95);
    }

    /*.home-btn-title {
        overflow-y: scroll;
        max-height: 100%;
    
    }*/

    /*.home-btn-title::-webkit-scrollbar  {
        width: 0px;
        display: none;
    }*/

    .h-120px {
        height: 120px;
        min-height: 120px;
        max-height: 120px;
    }

    .minh-120px {
        min-height: 120px;
    }

    .icon-check{
        width: 35px;
        height: 35px;
    }

    .divider-black {
        border-top: 1px solid rgb(196, 200, 203);
        opacity: 1;
    }
`;

function HomeButton(props) {
    const {
        title,
        primaryDescription,
        secondaryDescription,
        viewFunction = () => {},
        allowEdit = false,
        editFunction = () => {},
        allowDelete = false,
        deleteFunction = () => {},
        allowDownload = false,
        downloadFunction = () => {},
        allowHandleStatus = false,
        handleStatusFunction = () => {},
        check = false,
        status = '',
        isDownloaded = false,
        isCurationView = false,
    } = props;

    const { showAlert } = useContext(AlertContext);
    const [isCollapsed, setIsCollapsed] = useState(true);

    const ActionPill = ({ icon, label, color, onClick, active }) => (
        <div
            onClick={(e) => {
                e.stopPropagation(); // Evita disparar o viewFunction do HomeButton
                onClick();
            }}
            className="d-flex align-items-center rounded-pill px-3 py-1 custom-action-btn"
            style={{
                backgroundColor: active ? color : '#d9d9d9',
                color: '#535353',
                fontSize: '0.85rem',
                fontWeight: '500',
                cursor: active ? 'default' : 'pointer',
                opacity: active ? 1 : 0.9,
            }}
        >
            <MaterialSymbol icon={icon} size={18} className={label ? 'me-md-1' : ''} />
            <span className="d-none d-md-inline fw-medium">{label}</span>
        </div>
    );

    return (
        <div
            className="custom-btn rounded-4 d-flex flex-column justify-content-center font-barlow w-100 p-3 minh-120px"
            onClick={viewFunction}
        >
            <div className="d-flex align-items-center m-0">
                <div className="col home-btn-title">
                    <h5 className="text-break fs-6 lh-1 fw-medium pb-1 m-0">{title}</h5>
                    <p className="text-break fs-6 lh-1 fw-light pb-1 m-0">{primaryDescription || ''}</p>
                    {(allowDownload || secondaryDescription) && (
                        <p className="d-flex align-items-center text-break fs-6 lh-1 fw-light m-0">
                            {allowDownload && (
                                <MaterialSymbol
                                    icon={isDownloaded ? 'cloud_done' : 'cloud_off'}
                                    color={isDownloaded ? 'hsl(97, 43%, 70%)' : 'hsl(0, 0%, 50%)'}
                                    size={18}
                                    className="me-1"
                                />
                            )}
                            {secondaryDescription || ''}
                        </p>
                    )}
                </div>

                <div className="col-auto">
                    <div className="row d-flex flex-column flex-lg-row g-1">
                        <div className="col-12 col-lg-auto">
                            {allowDownload && !isDownloaded && !isCollapsed && (
                                <RoundedButton
                                    hsl={[97, 43, 70]}
                                    size={30}
                                    className="text-white"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        downloadFunction();
                                    }}
                                    icon="cloud_download"
                                />
                            )}
                        </div>
                        <div className="col-12 col-lg-auto">
                            {allowEdit && !isCollapsed && (
                                <RoundedButton
                                    hsl={[37, 98, 76]}
                                    size={30}
                                    className="text-white"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        editFunction();
                                    }}
                                    icon="edit"
                                />
                            )}
                        </div>
                        <div className="col-12 col-lg-auto order-lg-3">
                            {allowEdit && (
                                <RoundedButton
                                    hsl={[197, 43, 52]}
                                    size={30}
                                    className="text-white"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsCollapsed((prev) => !prev);
                                    }}
                                    icon={isCollapsed ? 'expand_content' : 'collapse_content'}
                                />
                            )}
                        </div>
                        <div className="col-12 col-lg-auto order-lg-2">
                            {allowDelete && !isCollapsed && (
                                <RoundedButton
                                    hsl={[355, 78, 66]}
                                    size={30}
                                    className="text-white"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        showAlert({
                                            headerText: 'Tem certeza que deseja excluir?',
                                            primaryBtnHsl: [355, 78, 66],
                                            primaryBtnLabel: 'Não',
                                            secondaryBtnHsl: [97, 43, 70],
                                            secondaryBtnLabel: 'Sim',
                                            onSecondaryBtnClick: () => deleteFunction(),
                                        });
                                    }}
                                    icon="delete"
                                />
                            )}
                        </div>
                    </div>
                </div>

                {check && (
                    <div className="col-auto ms-2">
                        <img src={CheckIcon} alt="Ícone de já respondido" className="icon-check" />
                    </div>
                )}
            </div>
            {isCurationView && (
                <div className="d-flex flex-column m-0">
                    <hr className="divider-black w-100 my-1" />
                    {allowHandleStatus && (
                        <div className="d-flex flex-wrap align-items-center justify-content-center gap-2 mt-2">
                            <ActionPill
                                icon="history"
                                label="Pendente"
                                color="#fedca9"
                                active={status === 'PENDING'}
                                onClick={() => handleStatusFunction('PENDING')}
                            />
                            <ActionPill
                                icon="check_circle"
                                label="Aprovar"
                                color="#abd392"
                                active={status === 'APPROVED'}
                                onClick={() => handleStatusFunction('APPROVED')}
                            />
                            <ActionPill
                                icon="cancel"
                                label="Reprovar"
                                color="#f59489"
                                active={status === 'REJECTED'}
                                onClick={() => handleStatusFunction('REJECTED')}
                            />
                        </div>
                    )}
                </div>
            )}

            <style>{styles}</style>
        </div>
    );
}

export default HomeButton;
